import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import classnames from 'classnames';
import { useParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import ReactSelect, { components, SingleValueProps } from 'react-select';

import { doTranslation } from '@/app/i18n/client';
import { FormError } from '@/utils/types';

import styles from '@/components/global/forms/select.module.scss';
import { theme } from '@/components/theme';

import { ThemeProvider } from '@mui/material/styles';

interface Props {
  async?: boolean;
  ariaDescribedby?: string;
  autoComplete?: string;
  autoHighlight?: boolean;
  dark?: boolean;
  defaultValue?: any;
  disabled?: boolean;
  error?: FormError;
  formatOptionLabel?: any;
  id?: string;
  inputId?: string;
  inputValue?: any;
  instanceId?: string;
  isSearchable?: boolean;
  isSortable?: boolean;
  isTripSearch?: boolean;
  label?: string;
  loadOptions?: any;
  mandatory?: boolean;
  name: string;
  onChange?: any;
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
  onInputChange?: any;
  options?: any;
  placeholder?: string;
  reference?: any;
  small?: boolean;
  subtitle?: string;
  passengerType?: string | null;
  value?: string;
  styles?: any;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  inlineLabel?: boolean;
  announcementText?: string;
}

const Select = (props: Props): React.JSX.Element => {
  const { lng } = useParams() as { lng: string };
  const { t } = doTranslation(lng);
  const {
    async,
    dark,
    disabled,
    error,
    isTripSearch,
    label,
    options,
    mandatory,
    name,
    subtitle,
    reference,
    small,
    announcementText,
    ...rest
  } = props;

  const [localError, setLocalError] = useState(error);
  const [ariaDescribedBy, setAriaDescribedBy] = useState<string | undefined>(undefined);
  const [fullRender, setFullRender] = useState(false);

  useEffect(() => {
    if (reference?.current) {
      const inputElement = reference.current.inputRef;
      if (inputElement) {
        inputElement.setAttribute('aria-required', mandatory ? 'true' : 'false');
      }
    }
    // Fallback timeout for rendering autocomplete if PerformanceObserver is not supported
    const timeout = setTimeout(() => {
      setFullRender(true);
    }, 1500);

    // PerformanceObserver to listen for the LCP event
    if (typeof PerformanceObserver === 'function') {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        for (const entry of entries) {
          if (entry.entryType === 'largest-contentful-paint') {
            setFullRender(true);
            observer.disconnect();
            clearTimeout(timeout);
            break;
          }
        }
      });

      observer.observe({ buffered: true, type: 'largest-contentful-paint' });

      return () => {
        observer.disconnect();
        clearTimeout(timeout);
      };
    } else {
      return () => {
        clearTimeout(timeout);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reference?.current?.inputRef && ariaDescribedBy) {
      reference.current.inputRef.setAttribute('aria-describedby', ariaDescribedBy);
    }
  }, [ariaDescribedBy, reference]);

  useEffect(() => {
    setLocalError(error);
  }, [error]);

  useEffect(() => {
    if (props.isSortable !== undefined) {
      const inputElement = reference?.current?.inputRef;
      if (inputElement && inputElement.hasAttribute('aria-readonly')) {
        inputElement.removeAttribute('aria-readonly');
      }
    }
  }, [reference, props.isSortable]);

  const RenderChevron = (props: any) => {
    const {
      selectProps: { menuIsOpen }
    } = props;
    return (
      <div
        className={classnames({
          [styles['chevron-dark-down']]: !menuIsOpen,
          [styles['chevron-dark-up']]: menuIsOpen
        })}
      ></div>
    );
  };

  const renderPassengerLabel = () => {
    if (props.passengerType === 'adult') {
      return (
        <>
          <label htmlFor={props.inputId}>{props.label}</label>
          <div className={styles.subtitle} id="age-description-adult">
            <span aria-hidden="true">{props.subtitle}</span>
            <span className="sr-only">{`${props.label} ${t('17 to 64 years')}`}</span>
          </div>
        </>
      );
    } else if (props.passengerType === 'senior') {
      return (
        <>
          <label htmlFor={props.inputId}>{props.label}</label>
          <div className={styles.subtitle} id="age-description-senior">
            <span aria-hidden="true">{props.subtitle}</span>
            <span className="sr-only">{`${props.label} ${t('65+ years')}`}</span>
          </div>
        </>
      );
    } else if (props.passengerType === 'child') {
      return (
        <>
          <label htmlFor={props.inputId}>{props.label}</label>
          <div className={styles.subtitle} id="age-description-child">
            <span aria-hidden="true">{props.subtitle}</span>
            <span className="sr-only">{`${props.label} ${t('2 to 16 years')}`}</span>
          </div>
        </>
      );
    } else if (props.passengerType === 'passengers') {
      return (
        <>
          <label htmlFor={props.inputId}>
            <span className="sr-only">{t('Number of Wheelchair')}</span>
            {props.label}
          </label>
        </>
      );
    }
  };

  const CustomSingleValue = <OptionType,>({ children, ...props }: SingleValueProps<OptionType>) => {
    return (
      <components.SingleValue
        {...props}
        innerProps={{
          ...props.innerProps,
          id: `${props.selectProps.inputId}-instructions`
        }}
      >
        {children}
        <span className="sr-only">{announcementText}</span>
      </components.SingleValue>
    );
  };

  const renderTextField = (params?: AutocompleteRenderInputParams) => {
    if (!async) {
      return null;
    }
    if (!fullRender) {
      return (
        <div className="MuiAutocomplete-root">
          <TextField
            className={classnames({ dark: dark })}
            error={localError ? true : false}
            id={name}
            InputLabelProps={{ shrink: false }}
            InputProps={{
              className: 'MuiAutocomplete-inputRoot'
            }}
            inputProps={{
              'aria-describedby': localError
                ? `${name}-error-message ${props.name}-instructions`
                : `${props.name}-instructions`,
              'aria-disabled': disabled === true ? 'true' : undefined,
              'aria-invalid': localError ? 'true' : undefined,
              'aria-required': mandatory === true ? 'true' : undefined,
              'autoComplete': props.autoComplete,
              'className': 'MuiAutocomplete-input'
            }}
            inputRef={reference}
            label={props.inputValue ? '' : props.placeholder}
            style={{ width: '100%' }}
            value={props.value}
          />
        </div>
      );
    }
    if (params && fullRender) {
      return (
        <TextField
          {...params}
          className={classnames({ dark: dark })}
          error={localError ? true : false}
          InputLabelProps={{ shrink: false }}
          inputProps={{
            ...params.inputProps,
            'aria-describedby': localError
              ? `${name}-error-message ${props.name}-instructions`
              : `${props.name}-instructions`,
            'aria-disabled': disabled === true ? 'true' : undefined,
            'aria-invalid': localError ? 'true' : undefined,
            'aria-required': mandatory === true ? 'true' : undefined,
            'autoComplete': props.autoComplete
          }}
          inputRef={reference}
          label={props.inputValue ? '' : props.placeholder}
        />
      );
    }
  };

  const renderSelect = () => {
    return (
      <>
        {!async && (
          <ReactSelect
            aria-describedby={localError ? `${name}-error-message` : undefined}
            aria-disabled={disabled === true ? 'true' : undefined}
            aria-invalid={localError ? 'true' : undefined}
            className={classnames({
              'react-select-container': true,
              'react-select-dark-small': small,
              'caret': !async,
              'dark': dark,
              'error': localError,
              'react-select-trip-search': isTripSearch
            })}
            classNamePrefix="react-select"
            components={{
              DropdownIndicator: RenderChevron,
              SingleValue: props.announcementText ? CustomSingleValue : components.SingleValue
            }}
            isDisabled={disabled}
            name={name}
            options={options}
            unstyled={true}
            ref={reference}
            onBlur={() => {
              if (props.value) {
                setLocalError(undefined);
              }
              if (props.announcementText) {
                setAriaDescribedBy(undefined);
              }
            }}
            onFocus={() => {
              if (props.announcementText) {
                setAriaDescribedBy(`${props.name}-instructions`);
              }
            }}
            aria-required={mandatory === true ? 'true' : 'false'}
            {...rest}
          />
        )}
        {async && fullRender && (
          <Autocomplete
            autoHighlight={props.autoHighlight}
            freeSolo
            id={name}
            options={props.options}
            onBlur={() => {
              if (props.value) {
                setLocalError(undefined);
              }
            }}
            onInputChange={(e, v) => {
              // @ts-ignore
              const value = e && e.target?.value;
              if (value || !v) {
                props?.onInputChange(value || '');
              }
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
              if (props.onKeyDown) {
                props.onKeyDown(e);
              }
            }}
            onChange={(e, v) => props.onChange(v)}
            renderInput={(params) => renderTextField(params)}
            filterOptions={(options) => options}
            renderOption={(props, option, { inputValue }) => {
              const highlight = (a: string, b: string) => {
                b = b.replace(/\./g, '\\.');
                const re = new RegExp(b, 'gi');
                return a.replace(re, (match: string) => `<strong>${match}</strong>`);
              };
              return (
                <li {...props} key={(props as any).key}>
                  <span dangerouslySetInnerHTML={{ __html: highlight((option as any).label, inputValue) }} />
                </li>
              );
            }}
            value={props.value}
          />
        )}
        {renderTextField()}
        {localError && (
          <div
            className={classnames({
              [styles['error-message']]: true,
              [styles.dark]: dark,
              [`${name}-error-message`]: localError
            })}
          >
            <span className="visually-hidden">{localError.screenReader}</span>
            {localError.normal}
            <span className="visually-hidden">{t('When you have corrected the input, press enter.')}</span>
          </div>
        )}
      </>
    );
  };

  const renderLabelWithOptionalSelect = (includeSelect: boolean) => (
    <label
      className={classnames({
        [styles.label]: true,
        [styles.dark]: dark,
        [styles.error]: localError,
        [styles['sort-label']]: includeSelect,
        [styles.inline]: props.inlineLabel
      })}
      htmlFor={name}
    >
      <span className={styles.text} id="label-text" tabIndex={-1}>
        {label ? label : ''} {label ? (mandatory ? '*' : '') : ''}
      </span>
      {includeSelect ? null : renderSelect()}
    </label>
  );

  return (
    <ThemeProvider theme={theme}>
      {props.passengerType === undefined ? (
        props.isSortable !== undefined ? (
          <div className={styles['sort']}>
            {renderLabelWithOptionalSelect(true)}
            <div className={styles['sort-options']}>{renderSelect()}</div>
          </div>
        ) : (
          renderLabelWithOptionalSelect(false)
        )
      ) : (
        <div className={styles.passenger}>
          <span className={styles.block}>{renderPassengerLabel()}</span>
          <ReactSelect
            components={{ DropdownIndicator: RenderChevron }}
            id={props.id}
            aria-describedby={props.ariaDescribedby}
            className={classnames({
              'react-select-container': true,
              'caret': !async,
              'dark': dark,
              'react-select-trip-search': isTripSearch
            })}
            classNamePrefix="react-select"
            isSearchable={false}
            name={name}
            options={options}
            unstyled={true}
            ref={reference}
            onBlur={() => {
              if (props.value) {
                setLocalError(undefined);
              }
            }}
            {...rest}
          />
        </div>
      )}
    </ThemeProvider>
  );
};

export default Select;
